import React from 'react';
import { SchematicShell, Rect } from './SchematicPrimitives';
import {
  COOKTOP_BURNER_HEAD_H,
  COOKTOP_GRATE_TOP,
  COOKTOP_GRATE_TUBE,
  COOKTOP_KNOB_RADIUS,
  COOKTOP_KNOB_H,
  COOKTOP_KNOB_Z_RATIO,
  cooktopKnobOffsets,
  cooktopBurnerPositions,
  cooktopBurnerRadius,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Elevation schematic for a drop-in `cooktop`.
 *
 * A cooktop is nearly all deck: `cooktopHeight` snaps it to a 3/4" slab, so
 * edge-on it is a thin band. What actually reads in an elevation is what
 * stands ON it — grates, burner heads, knobs — and those sit ABOVE the
 * object's bounding box, exactly as they do in 3D (the mesh exceeds
 * `dimensions.y` too). `SchematicShell` renders with `overflow: visible`, so
 * drawing above the box is legitimate rather than clipped.
 *
 * Every glyph is the true edge-on PROJECTION of the 3D mesh, from the same
 * spec constants:
 *   - grate ring (a torus) → a slim bar, 2R wide, tube-thick, at grate height
 *   - burner head (a low cylinder) → a short rect sitting on the glass
 *   - knob (an upright cylinder) → a small standing rect, 2r × knobH
 *
 * Burners at the same projected position (the front/back pair of a 2×2
 * deck seen from the front) collapse into ONE drawn stack — projection
 * merges them in reality, and double-stroking the same bar just renders
 * darker lines.
 */
export function CooktopSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const isSide = view === 'left' || view === 'right';
  const viewportW = isSide ? depth : width;

  // Shared with the renderer — same layout, same count.
  const burners = cooktopBurnerPositions(width, depth);
  const burnerR = cooktopBurnerRadius(width, depth);

  // Front elevation reads across the width; a side view reads across the
  // depth, so each burner is placed by whichever axis faces the viewer.
  const along = ([xo, zo]) => (isSide ? depth / 2 + zo : width / 2 + xo);

  // Collapse burners that project to the same spot (front/back pairs in a
  // front view; left/right pairs in a side view).
  const clusterXs = [
    ...new Set(burners.map((p) => Math.round(along(p) * 1000) / 1000)),
  ];

  // SVG y grows downward and the deck's top edge is y = 0, so anything
  // standing on the deck has a NEGATIVE y.
  const grateTopY = -COOKTOP_GRATE_TOP;
  const grateBarH = COOKTOP_GRATE_TUBE * 2;
  const headTopY = -COOKTOP_BURNER_HEAD_H;
  const knobTopY = -COOKTOP_KNOB_H;

  // One knob per burner, grouped in a center-front strip (renderer offsets).
  // The strip is drawn only when it FITS between the burner columns: on a
  // 36"+ deck the center burner sits exactly behind it, and an orthographic
  // projection would bury the strip in the grate — the drafter's move is to
  // omit the sub-inch knobs there, not to weave them through the burner.
  // Presence is a pure function of the same shared spec both views read.
  const stripXs = cooktopKnobOffsets(burners.length).map(
    (xo) => width / 2 + xo
  );
  const stripClearsBurners = clusterXs.every(
    (cx) =>
      cx + burnerR < stripXs[0] - COOKTOP_KNOB_RADIUS ||
      cx - burnerR > stripXs[stripXs.length - 1] + COOKTOP_KNOB_RADIUS
  );
  const knobXs = isSide
    ? // All knobs share the front-edge z, so a side view shows exactly one.
      [depth / 2 + depth * COOKTOP_KNOB_Z_RATIO]
    : stripClearsBurners
      ? stripXs
      : [];

  return (
    <SchematicShell
      width={viewportW}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {clusterXs.map((cx, i) => (
        <g key={`burner-${i}`} data-part="burner">
          {/* Grate ring edge-on: a slim bar of ring stock, floating at
              grate height exactly as the torus does in 3D. */}
          <Rect
            x={cx - burnerR}
            y={grateTopY}
            width={burnerR * 2}
            height={grateBarH}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
          {/* The low burner head under it, on the glass. */}
          <Rect
            x={cx - burnerR * 0.6}
            y={headTopY}
            width={burnerR * 1.2}
            height={COOKTOP_BURNER_HEAD_H}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      ))}

      {/* Knobs: upright cylinders in 3D, so edge-on each is a small
          standing rect. The knob row sits at the deck's FRONT edge —
          7" in front of the glass — so in an orthographic elevation it
          occludes the burner behind it: drawn last, filled with the
          paper white (the elevation canvas is white). */}
      {knobXs.map((cx, i) => (
        <g key={`knob-${i}`} data-part="knob">
          <Rect
            x={cx - COOKTOP_KNOB_RADIUS}
            y={knobTopY}
            width={COOKTOP_KNOB_RADIUS * 2}
            height={COOKTOP_KNOB_H}
            fill="white"
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      ))}
    </SchematicShell>
  );
}

CooktopSchematic.supportsSideViews = true;
